/**
 * Changes (Diff) — compare active POR snapshot vs previous (Doc 17 §12).
 * Plan-cycle scoped list shell with §1E zero/one/few/many (CollectionView).
 */

import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { GitCompare } from 'lucide-react';
import { EntityListPage } from '../../components/collection';
import type { SortOption } from '../../components/collection/types';
import { getSnapshotsForPlanCycle } from '../../lib/planningService';
import { buildPlanDiffMetrics, type DiffMetric } from '../../lib/planDiff';
import type { PlanOfRecordSnapshot } from '../../types/planning';
import {
  PlanningCycleScope,
  type PlanningCycleContext,
} from '../../components/planning/PlanningCycleScope';

const SORT_OPTIONS: SortOption<string>[] = [
  { field: 'label', label: 'Metric' },
  { field: 'value', label: 'Count' },
];

function ChangesContent({ ctx }: { ctx: PlanningCycleContext }) {
  const navigate = useNavigate();
  const [snapshots, setSnapshots] = useState<PlanOfRecordSnapshot[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    getSnapshotsForPlanCycle(ctx.cycleId).then(({ data }) => {
      setSnapshots(data ?? []);
      setLoading(false);
    });
  }, [ctx.cycleId]);

  const latest = snapshots[0];
  const previous = snapshots[1];
  const hasDiff = latest && previous;

  const metrics = useMemo(
    () => (hasDiff ? buildPlanDiffMetrics(latest.payload, previous.payload) : []),
    [hasDiff, latest, previous],
  );

  const emptyTitle =
    snapshots.length === 0
      ? 'No snapshots yet'
      : 'Only one snapshot';
  const emptyDescription =
    snapshots.length === 0
      ? 'Authorize a Plan of Record to create the first snapshot. A diff appears when a second POR is authorized.'
      : 'Changes appear after a new Plan of Record is authorized and supersedes the prior snapshot.';

  return (
    <EntityListPage<DiffMetric, string>
      items={metrics}
      loading={loading}
      getId={(m) => m.id}
      masterDetail={false}
      title="Changes (Diff)"
      subtitle={
        hasDiff
          ? `Comparing ${new Date(latest.authorized_at).toLocaleDateString()} vs ${new Date(previous.authorized_at).toLocaleDateString()}`
          : `${snapshots.length} snapshot${snapshots.length !== 1 ? 's' : ''} on this plan cycle`
      }
      icon={<GitCompare className="h-4 w-4 text-primary-600" />}
      iconBgClassName="bg-primary-50"
      itemLabel="metric"
      searchFields={['label']}
      searchPlaceholder="Search diff metrics…"
      sortOptions={SORT_OPTIONS}
      defaultSort={{ field: 'label', direction: 'asc' }}
      viewModes={['table']}
      defaultViewMode="table"
      selectable={false}
      tableColumns={[
        {
          key: 'label',
          label: 'Metric',
          sortable: true,
          minWidth: 240,
          render: (m) => <span className="text-sm text-[var(--text-sec)] dark:text-[var(--text-sec)]">{m.label}</span>,
        },
        {
          key: 'value',
          label: 'Count',
          sortable: true,
          align: 'right',
          minWidth: 96,
          render: (m) => <span className="tabular-nums font-semibold">{m.value}</span>,
        },
      ]}
      emptyTitle={emptyTitle}
      emptyDescription={emptyDescription}
      emptyAction={
        !hasDiff && !loading
          ? { label: 'View plan', onClick: () => navigate(`/planning/plans/${ctx.cycleId}`) }
          : undefined
      }
      contextBar={{
        label: `Plan cycle · ${snapshots.length} snapshot${snapshots.length !== 1 ? 's' : ''}`,
      }}
      afterToolbar={
        !loading && !hasDiff ? (
          <div className="px-4 pb-2">
            <Link to={`/planning/plans/${ctx.cycleId}`} className="text-sm text-primary-600 hover:underline">
              View plan →
            </Link>
          </div>
        ) : undefined
      }
      shellClassName="h-full"
    />
  );
}

export default function PlanningChanges() {
  const { cycleId: cycleIdFromParams } = useParams<{ cycleId?: string }>();
  const [searchParams] = useSearchParams();
  const [selectedCycleId, setSelectedCycleId] = useState<string | null>(null);
  const resolvedCycleId = cycleIdFromParams ?? searchParams.get('cycleId');

  return (
    <div className="h-full min-h-[520px]">
      <PlanningCycleScope
        cycleIdParam={resolvedCycleId}
        selectedCycleId={selectedCycleId}
        onSelectCycle={(id) => setSelectedCycleId(id)}
      >
        {(ctx) => <ChangesContent ctx={ctx} />}
      </PlanningCycleScope>
    </div>
  );
}
